import {useEffect, useRef, useState} from "react";

// The three types a request can have, in the dashboard's order, named as the menu names them so French reads Dépenses and C-Crédit (XPEN-3755).
const REQUEST_TYPES = [
    {id: "invoices", labelKey: "invoice_types.ap", fallback: "AP"},
    {id: "cards", labelKey: "c-card", fallback: "C-Card"},
    {id: "expenses", labelKey: "expenses", fallback: "Expenses"},
];

// Same reading of the invoice type as the dashboard's type column: XPENSE is an expense report,
// p-card a credit-card statement, and anything else, including none, is AP.
export const requestTypeOf = (request) => {
    const invoiceType = String(request?.invoice_type ?? "").toLowerCase();
    if (invoiceType === "xpense") return "expenses";
    if (invoiceType === "p-card") return "cards";
    return "invoices";
};

// No type ticked means every type.
export const matchesRequestTypes = (request, types) => types.length === 0 || types.includes(requestTypeOf(request));

const TypeFilter = ({value, onChange, translate}) => {
    const [open, setOpen] = useState(false);
    const container = useRef(null);
    const allLabel = translate("approval_page.type_filter_all", "All request types");
    const name = translate("approval_page.type_filter", "Request type");
    const label = (type) => translate(type.labelKey, type.fallback);

    useEffect(() => {
        if (!open) return undefined;
        const closeOnOutsideClick = (event) => {
            if (container.current && !container.current.contains(event.target)) setOpen(false);
        };
        const closeOnEscape = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", closeOnOutsideClick);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("mousedown", closeOnOutsideClick);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [open]);

    const toggleType = (id) => {
        const next = value.includes(id) ? value.filter((type) => type !== id) : [...value, id];
        // Every type ticked is the same as none: back to the default.
        onChange(next.length === REQUEST_TYPES.length ? [] : next);
    };

    const summary = value.length === 0
        ? allLabel
        : REQUEST_TYPES.filter((type) => value.includes(type.id)).map(label).join(", ");

    return (
        <div ref={container} className="position-relative" style={{maxWidth: "100%"}}>
            <button
                type="button"
                aria-label={name}
                aria-haspopup="true"
                aria-expanded={open}
                className="form-control form-control-sm d-flex align-items-center justify-content-between text-left"
                style={{width: 220, maxWidth: "100%"}}
                onClick={() => setOpen((isOpen) => !isOpen)}
            >
                <span className="text-truncate" style={{minWidth: 0}}>{summary}</span>
                <span aria-hidden="true" data-type-filter-chevron className="dropdown-toggle ml-2" />
            </button>
            {open && (
                <div
                    role="group"
                    aria-label={name}
                    data-type-filter-menu
                    className="dropdown-menu show p-2"
                    style={{left: 0, right: "auto", width: "100%", minWidth: 0}}
                >
                    <label className="d-flex align-items-center mb-1" style={{gap: 8, cursor: "pointer"}}>
                        <input type="checkbox" checked={value.length === 0} onChange={() => onChange([])} />
                        <span>{allLabel}</span>
                    </label>
                    {REQUEST_TYPES.map((type) => (
                        <label key={type.id} className="d-flex align-items-center mb-1" style={{gap: 8, cursor: "pointer"}}>
                            <input type="checkbox" checked={value.includes(type.id)} onChange={() => toggleType(type.id)} />
                            <span>{label(type)}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TypeFilter;
